
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { formatUTCForDisplay } from "@/utils/timezoneUtils";

export interface Booking {
  id: string;
  service_name?: string;
  duration_minutes: number;
  location?: string;
  address?: string;
  booking_date: string;
  start_time: string;
  end_time: string;
  status: string;
  price: number;
  currency: string;
  customer_name: string;
  customer_email: string;
  provider_id: string;
  service_id: string;
  user_id: string;
  notes?: string;
  checked_in_at?: string;
  checked_out_at?: string;
  providers?: {
    name: string;
    address: string;
  };
  services?: {
    name: string;
  };
}

export const useBookings = () => {
  return useQuery({
    queryKey: ["bookings"],
    queryFn: async () => {
      console.log("Fetching user bookings...");
      
      const { data: { user } } = await supabase.auth.getUser();
      console.log("Current user:", user?.email);
      
      if (!user) {
        console.log("No authenticated user found");
        return [];
      }

      // Fetch bookings with related provider and service information - NOW INCLUDING check-in/out fields
      const { data: bookings, error } = await supabase
        .from('bookings')
        .select(`
          *,
          providers:provider_id(name, address),
          services:service_id(name)
        `)
        .order('booking_date', { ascending: false })
        .order('start_time', { ascending: false });

      console.log("Bookings query result:", bookings);
      console.log("Bookings query error:", error);

      if (error) {
        console.error('Error fetching bookings:', error);
        throw error;
      }

      // Transform the data to match the expected format
      const transformedBookings: Booking[] = (bookings || []).map(booking => {
        // Times are now stored as UTC times in HH:MM:SS format, convert to local display
        // Create a full UTC ISO string for proper conversion
        const bookingStartUTC = `${booking.booking_date}T${booking.start_time}Z`;
        const bookingEndUTC = `${booking.booking_date}T${booking.end_time}Z`;
        
        // Convert to local time for display
        const localStartTime = new Date(bookingStartUTC).toLocaleTimeString('en-GB', { 
          hour12: false, 
          hour: '2-digit', 
          minute: '2-digit'
        });
        const localEndTime = new Date(bookingEndUTC).toLocaleTimeString('en-GB', { 
          hour12: false, 
          hour: '2-digit', 
          minute: '2-digit'
        });
        
        console.log(`Booking ${booking.id} time conversion:`, {
          storedStart: booking.start_time,
          storedEnd: booking.end_time,
          utcStart: bookingStartUTC,
          utcEnd: bookingEndUTC,
          localStart: localStartTime,
          localEnd: localEndTime
        });

        return {
          id: booking.id,
          service_name: booking.services?.name || 'Unknown Service',
          duration_minutes: booking.duration_minutes,
          location: booking.providers?.name || 'Unknown Location',
          address: booking.providers?.address || '',
          booking_date: booking.booking_date,
          start_time: localStartTime,
          end_time: localEndTime,
          status: booking.status === 'confirmed' ? 'Confirmed' : 
                  booking.status === 'completed' ? 'Completed' : 
                  booking.status === 'cancelled' ? 'Cancelled' : booking.status,
          price: Number(booking.price),
          currency: booking.currency || 'GBP',
          customer_name: booking.customer_name,
          customer_email: booking.customer_email,
          provider_id: booking.provider_id,
          service_id: booking.service_id,
          user_id: booking.user_id,
          notes: booking.notes,
          checked_in_at: booking.checked_in_at,
          checked_out_at: booking.checked_out_at,
          providers: booking.providers,
          services: booking.services
        };
      });

      console.log("Transformed bookings with local time display and check-in status:", transformedBookings);
      return transformedBookings;
    },
  });
};
